import React, { useState, useContext, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { firstBy } from 'thenby';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import AppContext from '../Hooks/AppContext';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from '@mui/x-date-pickers';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

const dateTimePickerSlotProps = {
  textField: {
    // variant: 'outlined',
    InputProps: {readOnly: true},
    onBeforeInput: evt => evt?.preventDefault() && evt?.stopPropagation(),
  },
};

const BeneficiaryContract = props => {
  /**
   * Constants
   */

  /**
   * States
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [defaultData, setDefaultData] = useState(null);
  const [data, setData] = useState(null);
  const [deps, setDeps] = useState(null);
  const [notification, setNotification] = useState(null);

  /**
   * Hooks
   */
  const ac = useContext(AppContext);

  /**
   * Effects
   */
  useEffect(() => {
    ac.ajax('getBeneficiaryContract', {
      idBeneficiary: props.value.idBeneficiary,
      idBeneficiaryContract: props.value.id,
    })
      .then(response => {
        if (response.status.ok) {
          if (Boolean(response.result.data.beneficiaryContract.id)) {
            setDefaultData(response.result.data.beneficiaryContract);
            setData(response.result.data.beneficiaryContract);
          } else {
            setDefaultData(response.result.defaultValues.beneficiaryContract);
            setData({
              ...response.result.data.beneficiaryContract,
              idBeneficiary: props.value.idBeneficiary,
              validFrom: (new Date()).format('YYYY-MM-DD'),
            });
          }

          setDeps(response.result.dependencies);

          setIsLoaded(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    let message = null;

    message = message !== null ? message : data.idBeneficiary === '' ? 'Nu ati selectat beneficiarul' : message;
    message = message !== null ? message : !isInsuranceHouse(data.idBeneficiary) && data.idBranch === '' ? 'Nu ati selectat punctul de lucru' : message;
    message = message !== null ? message : data.contract === '' ? 'Nu ati completat numele contractului' : message;

    message = message !== null ? message : JSON.stringify(data) === JSON.stringify(defaultData) ? 'Nu ati efectuat nici o modificare' : null;

    setNotification(message);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, defaultData, data]);

  /**
   * Getters
   */
  const getBranches = useCallback(() => {
    return deps.branches.sort(firstBy('branch'));
  }, [deps]);

  const isInsuranceHouse = idBeneficiary => Boolean(idBeneficiary) ? deps.beneficiaries.find(item => item.id === idBeneficiary)?.isInsuranceHouse : null;

  /**
   * Handlers
   */
  const handleDataChange = () => evt => {
    let mods = {};
    switch (evt.target.name) {
      case 'isSchedulable':
        mods.dailySchedulesLimit = '0';
        break;
      default: break;
    }

    let value = null;

    switch (evt.target.type) {
      case 'checkbox':
        value = evt.target.checked;
        break;
      case 'datePicker':
        value = evt.target.value.$d.format('YYYY-MM-DD');
        break;
      default:
        value = evt.target.value;
    }

    setData(prevState => ({
      ...prevState,
      [evt.target.name]: value,
      ...mods,
    }));
  };

  const handleClose = (evt, reason) => {
    switch (reason) {
      case 'backdropClick':
        break;
      case 'escapeKeyDown':
        if (JSON.stringify(data) === JSON.stringify(defaultData)) {
          handleCancel()();
        }
        break;
      default: break;
    }
  };

  const handleCancel = () => () => props.onCancel();
  const handleConfirm = () => () => {
    ac.ajax('saveBeneficiaryContract', data)
      .then(response => {
        if (response.status.ok) {
          props.onConfirm(response);
        }
      });
  };

  /**
   * Renderer
   */
  if (!isLoaded) {
    return (
      <Backdrop open sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Dialog maxWidth='sm' open keepMounted scroll='paper'
      disableEscapeKeyDown={ac.isLoading}
      onClose={handleClose}
    >
      {/**
       * Title
      */}
      <DialogTitle>Contract beneficiar: {defaultData.id === '' ? 'Adaugare' : 'Modificare'}</DialogTitle>

      {/**
       * Content
      */}
      <DialogContent>
        <DialogContentText>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <TextField disabled
                label='ID'
                placeholder='ID'
                name='id'
                value={data.id}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label='Societate'
                placeholder='Societate'
                value={deps.beneficiary.beneficiary}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField select
                disabled={ac.isLoading || isInsuranceHouse(data.idBeneficiary)}
                label='Punct de lucru'
                placeholder='Punct de lucru'
                name='idBranch'
                value={data.idBranch}
                onChange={handleDataChange()}
              >
                {getBranches().map(item => (
                  <MenuItem key={item.id} value={item.id}><Typography variant='body1' sx={{fontWeight: item.isDefault ? 'bold' : 'initial'}}>{item.branch}</Typography></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField required autoFocus
                disabled={ac.isLoading}
                label='Contract'
                placeholder='Contract'
                name='contract'
                value={data.contract}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={4}>
              <DatePicker slotProps={dateTimePickerSlotProps} maxDate={dayjs(data.validTo)}
                disabled={ac.isLoading}
                label='De la data'
                value={dayjs(data.validFrom)}
                onChange={newValue => handleDataChange()({target: {type: 'datePicker', name: 'validFrom', value: newValue}})}
              />
            </Grid>
            <Grid item xs={4}>
              <DatePicker slotProps={dateTimePickerSlotProps} minDate={dayjs(data.validFrom)}
                disabled={ac.isLoading}
                label='Pana la data'
                value={dayjs(data.validTo)}
                onChange={newValue => handleDataChange()({target: {type: 'datePicker', name: 'validTo', value: newValue}})}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField required type='number' inputProps={{min: '0', max: '90', step: '1'}}
                disabled={ac.isLoading || isInsuranceHouse(data.idBeneficiary)}
                label='Zile scadente'
                placeholder='Zile scadente'
                name='dueDays'
                value={data.dueDays}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                label='Permite facturarea la termen'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading || isInsuranceHouse(data.idBeneficiary)}
                    name='allowCredit'
                    checked={data.allowCredit}
                    onChange={handleDataChange()}
                  />
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField required type='number' inputProps={{min: data.reservedValue, step: '0.01', style: {textAlign: 'right'}}}
                InputProps={{endAdornment: <InputAdornment position='end'>RON</InputAdornment>}}
                disabled={ac.isLoading}
                label='Valoare contract'
                placeholder='Valoare contract'
                name='signedValue'
                value={data.signedValue}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField required type='number' inputProps={{min: '0', max: data.signedValue, step: '0.01', style: {textAlign: 'right'}}}
                InputProps={{endAdornment: <InputAdornment position='end'>RON</InputAdornment>}}
                disabled={ac.isLoading || !isInsuranceHouse(data.idBeneficiary)}
                error={+data.reservedValue > +data.signedValue}
                label='Valoare rezervata'
                placeholder='Valoare rezervata'
                name='reservedValue'
                value={data.reservedValue}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={9}>
              <FormControlLabel
                label='Serviciile de pe acest contract se pot programa'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='isSchedulable'
                    checked={data.isSchedulable}
                    onChange={handleDataChange()}
                  />
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField required type='number' inputProps={{min: '0', max: '255', step: '1'}}
                disabled={ac.isLoading || !data.isSchedulable}
                label='Limita zilnica'
                placeholder='Limita zilnica'
                name='dailySchedulesLimit'
                value={data.dailySchedulesLimit}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField multiline rows={3}
                disabled={ac.isLoading}
                label='Observatii'
                placeholder='Observatii'
                name='description'
                value={data.description}
                onChange={handleDataChange()}
              />
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>

      <DialogContent>
        <DialogContentText color='error' sx={{visibility: Boolean(notification) ? 'initial' : 'hidden'}}>
          {Boolean(notification) ? notification : 'Totul pare OK!'}
        </DialogContentText>
      </DialogContent>

      {/**
       * Actions
      */}
      <DialogActions>
        <Button disabled={ac.isLoading || Boolean(notification)} onClick={handleConfirm()}>Salveaza</Button>
        <Button variant='text' color='inherit' disabled={ac.isLoading} onClick={handleCancel()}>Renunta</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BeneficiaryContract;